import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { 
  ChevronLeft, 
  ChevronRight, 
  X, 
  CalendarOff,
  CalendarCheck,
  Users,
  Info
} from 'lucide-react';
import './AdminCalendar.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminCalendar = () => {
  const { token } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [blockedDates, setBlockedDates] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [blockReason, setBlockReason] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCalendarData();
  }, [currentDate]);

  const fetchCalendarData = async () => {
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const startDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      const endDate = `${year}-${String(month + 1).padStart(2, '0')}-31`;

      const [blockedRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/admin/calendar/blocked-dates?start_date=${startDate}&end_date=${endDate}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/calendar/bookings`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setBlockedDates(blockedRes.data.blocked_dates || []);
      setBookings(bookingsRes.data.bookings || []);
    } catch (error) {
      console.error('Error fetching calendar data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleDateClick = (date) => {
    const dateStr = formatDateString(date);
    setSelectedDate({ date, dateStr });
    setBlockReason('');
    setShowModal(true);
  };

  const formatDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const isDateBlocked = (date) => {
    const dateStr = formatDateString(date);
    return blockedDates.some(bd => bd.date === dateStr);
  };

  const getBlockedDateInfo = (date) => {
    const dateStr = formatDateString(date);
    return blockedDates.find(bd => bd.date === dateStr);
  };

  const getBookingsForDate = (date) => {
    const dateStr = formatDateString(date);
    return bookings.filter(b => {
      if (!b.event_date) return false;
      return b.event_date.startsWith(dateStr);
    });
  };

  const handleBlockDate = async () => {
    if (!selectedDate) return;

    try {
      await axios.post(
        `${API}/admin/calendar/blocked-dates`,
        { date: selectedDate.dateStr, reason: blockReason || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      fetchCalendarData();
      setShowModal(false);
    } catch (error) {
      console.error('Error blocking date:', error);
      alert(error.response?.data?.detail || 'Failed to block date');
    }
  };

  const handleUnblockDate = async () => {
    if (!selectedDate) return;

    try {
      await axios.delete(
        `${API}/admin/calendar/blocked-dates/${selectedDate.dateStr}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      fetchCalendarData();
      setShowModal(false);
    } catch (error) {
      console.error('Error unblocking date:', error);
      alert('Failed to unblock date');
    }
  };

  const renderCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const days = [];
    
    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const isBlocked = isDateBlocked(date);
      const blockedInfo = getBlockedDateInfo(date);
      const dayBookings = getBookingsForDate(date);
      const isPast = date < today;
      const isToday = date.toDateString() === today.toDateString();

      days.push(
        <div
          key={day}
          className={`calendar-day ${isBlocked ? 'blocked' : ''} ${isPast ? 'past' : ''} ${isToday ? 'today' : ''} ${dayBookings.length > 0 ? 'has-bookings' : ''}`}
          onClick={() => !isPast && handleDateClick(date)}
          data-testid={`calendar-day-${day}`}
        >
          <span className="day-number">{day}</span>
          
          {isBlocked && (
            <div className="day-blocked-indicator" title={blockedInfo?.reason || 'Blocked'}>
              <CalendarOff size={14} />
            </div>
          )}
          
          {dayBookings.length > 0 && (
            <div className="day-bookings-indicator">
              <Users size={12} />
              <span>{dayBookings.length}</span>
            </div>
          )}
        </div>
      );
    }

    return days;
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  if (loading) {
    return <div className="admin-calendar-loading">Loading calendar...</div>;
  }

  return (
    <div className="admin-calendar">
      <div className="calendar-header-section">
        <div>
          <h2>Availability Calendar</h2>
          <p>Manage blocked dates and view upcoming bookings</p>
        </div>
        <div className="calendar-legend">
          <div className="legend-item">
            <span className="legend-color blocked"></span>
            <span>Blocked</span>
          </div>
          <div className="legend-item">
            <span className="legend-color has-bookings"></span>
            <span>Has Bookings</span>
          </div>
          <div className="legend-item">
            <span className="legend-color today"></span>
            <span>Today</span>
          </div>
        </div>
      </div>

      <div className="calendar-container">
        <div className="calendar-nav">
          <button onClick={handlePrevMonth} className="nav-btn" data-testid="prev-month-btn">
            <ChevronLeft size={20} />
          </button>
          <h3 className="current-month">
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h3>
          <button onClick={handleNextMonth} className="nav-btn" data-testid="next-month-btn">
            <ChevronRight size={20} />
          </button>
        </div>

        <div className="calendar-grid">
          <div className="calendar-weekdays">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="weekday">{day}</div>
            ))}
          </div>
          <div className="calendar-days">
            {renderCalendar()}
          </div>
        </div>
      </div>

      <div className="blocked-dates-list">
        <h3>Blocked Dates This Month</h3>
        {blockedDates.length > 0 ? (
          <div className="blocked-dates-items">
            {blockedDates.map(bd => (
              <div key={bd.id} className="blocked-date-item">
                <div className="blocked-date-info">
                  <CalendarOff size={16} />
                  <span className="blocked-date-value">{bd.date}</span>
                  {bd.reason && <span className="blocked-date-reason">- {bd.reason}</span>}
                </div>
                <button
                  onClick={() => {
                    setSelectedDate({ date: new Date(bd.date), dateStr: bd.date });
                    handleUnblockDate();
                  }}
                  className="unblock-btn"
                >
                  Unblock
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="no-blocked-dates">No blocked dates this month</p>
        )}
      </div>

      {/* Date Modal */}
      {showModal && selectedDate && (
        <div className="calendar-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="calendar-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowModal(false)}>
              <X size={24} />
            </button>

            <div className="modal-header">
              <h3>
                {selectedDate.date.toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </h3>
            </div>

            <div className="modal-content">
              {isDateBlocked(selectedDate.date) ? (
                <div className="date-status blocked">
                  <CalendarOff size={24} />
                  <div>
                    <h4>This date is blocked</h4>
                    {getBlockedDateInfo(selectedDate.date)?.reason && (
                      <p>Reason: {getBlockedDateInfo(selectedDate.date).reason}</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="date-status available">
                  <CalendarCheck size={24} />
                  <div>
                    <h4>This date is available</h4>
                    <p>Customers can book on this date</p>
                  </div>
                </div>
              )}

              {/* Bookings for this date */}
              {getBookingsForDate(selectedDate.date).length > 0 && (
                <div className="date-bookings">
                  <h4>Bookings on this date:</h4>
                  {getBookingsForDate(selectedDate.date).map(booking => (
                    <div key={booking.id} className="booking-item">
                      <div className="booking-customer">{booking.customer_name}</div>
                      <div className="booking-details">
                        {booking.package_name} • {booking.status}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Block/Unblock Form */}
              {!isDateBlocked(selectedDate.date) ? (
                <div className="block-form">
                  <label>Block this date?</label>
                  <input
                    type="text"
                    value={blockReason}
                    onChange={(e) => setBlockReason(e.target.value)}
                    placeholder="Reason (optional)"
                  />
                  <button onClick={handleBlockDate} className="btn-block">
                    <CalendarOff size={18} />
                    Block Date
                  </button>
                </div>
              ) : (
                <div className="unblock-form">
                  <button onClick={handleUnblockDate} className="btn-unblock">
                    <CalendarCheck size={18} />
                    Unblock Date
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCalendar;
